/**
 * The QUESTION-LEVEL evaluation (0105) — a second, independent evaluation of an interview.
 *
 * The competency evaluation (evaluation.ts) asks "how strong is the candidate on each criterion?". This one
 * asks "how well did the candidate answer each question, for this role?": every answered question gets a
 * 0-100 score from the role (title + description) and that question alone — no competency, no anchor — and
 * the counted scores are averaged into the INTERVIEW score.
 *
 * Two evaluations, two numbers, never blended: the interview score is not an input to capability or coverage,
 * and capability is not an input to the interview score. Which one a list is ranked by is a per-organization
 * setting (RankingBasis); both are always shown.
 *
 * The scoring is content only — it never scores accent, tone, fluency, grammar, pace or confidence (GROUND_RULES §4).
 * A machine-settled answer (a code run, a snippet pick) is scored by CODE from its result, never by a model.
 */
/** Who produced a question's score. */
export const QUESTION_SCORE_SOURCES = ["model", "code_run", "snippet_pick"];
/** What the organization's lists, buckets and headline rank by. */
export const RANKING_BASES = ["interview", "competency"];
export const DEFAULT_RANKING_BASIS = "interview";
export const isRankingBasis = (v) => RANKING_BASES.includes(v);
export const RANKING_BASIS_LABEL = {
    interview: "Interview score",
    competency: "Competency score",
};
/** Whole-number average of the counted, scored questions. `total` is every counted question the interview had. */
export function interviewScoreOf(scores, total) {
    const counted = scores.filter((s) => s.counts && s.score !== null);
    const sum = counted.reduce((n, s) => n + s.score, 0);
    return {
        score: counted.length > 0 ? Math.round(sum / counted.length) : null,
        scored: counted.length,
        total: Math.max(total, counted.length),
    };
}
/** Below this share of the counted questions scored, an interview reads "Limited" and ranks below full ones. */
export const INTERVIEW_LIMITED_SHARE = 0.6;
export const INTERVIEW_STRONG_MIN = 70;
export const INTERVIEW_MIXED_MIN = 40;
export const INTERVIEW_CLASS_LABEL = {
    strong: "Strong answers",
    mixed: "Mixed answers",
    weak: "Weak answers",
    limited: "Limited",
    none: "Not scored yet",
};
/** The plain-language class of an interview score. "Limited" wins over the score band when too few questions were scored. */
export function interviewClassOf(i) {
    if (i.score === null)
        return "none";
    if (i.total > 0 && i.scored / i.total < INTERVIEW_LIMITED_SHARE)
        return "limited";
    if (i.score >= INTERVIEW_STRONG_MIN)
        return "strong";
    if (i.score >= INTERVIEW_MIXED_MIN)
        return "mixed";
    return "weak";
}
/** A code answer's score: the share of test cases that passed — ground truth from the sandbox, never a guess. */
export function codeRunScore(passed, total) {
    if (!Number.isFinite(passed) || !Number.isFinite(total) || total <= 0)
        return null;
    return Math.max(0, Math.min(100, Math.round((passed / total) * 100)));
}
/** A snippet-pick answer's score: the stored correct option is settled fact. Right = 100, wrong = 0. */
export const snippetPickScore = (correct) => (correct ? 100 : 0);
/** The question kinds whose scores are shown but left out of the average (every candidate gets a different set). */
export const UNCOUNTED_QUESTION_KINDS = ["adaptive_challenge"];
export const questionCounts = (q) => !UNCOUNTED_QUESTION_KINDS.includes(q.kind) && q.source !== "adaptive";
const clamp100 = (n) => typeof n === "number" && Number.isFinite(n) ? Math.max(0, Math.min(100, Math.round(n))) : null;
/**
 * Turn the scorer's raw reply into stored scores. A rating for a question that was not asked, was not
 * answered, or repeats an earlier one is dropped; the score is clamped; the reason is trimmed; a quote that
 * cannot be found verbatim in the answer is dropped (the score stays, the span becomes null) — the same
 * discipline as competency evidence. `locate` is evaluation.ts's locateSpan, passed in so this file stays
 * free of an import cycle. Every correction is noted.
 */
export function validateQuestionScores(raw, answers, locate) {
    const byId = new Map(answers.map((a) => [a.questionId, a]));
    const seen = new Set();
    const notes = [];
    const scores = [];
    for (const r of Array.isArray(raw) ? raw : []) {
        const a = r && typeof r.questionId === "string" ? byId.get(r.questionId) : undefined;
        if (!a || !a.answerText) {
            notes.push(`question score for ${r?.questionId ?? "?"} dropped: no such answered question`);
            continue;
        }
        if (seen.has(a.questionId)) {
            notes.push(`question score for ${a.questionId} dropped: given twice`);
            continue;
        }
        const score = clamp100(r.score);
        if (score === null) {
            notes.push(`question score for ${a.questionId} dropped: no numeric score`);
            continue;
        }
        seen.add(a.questionId);
        let span = null;
        if (typeof r.span === "string" && r.span.trim()) {
            span = locate(r.span, a.answerText);
            if (!span)
                notes.push(`question score for ${a.questionId}: quote could not be found in the answer — dropped`);
        }
        const reason = typeof r.reason === "string" ? r.reason.replace(/\s+/g, " ").trim().slice(0, 280) : "";
        scores.push({ questionId: a.questionId, seq: a.seq, score, reason, span, source: "model", counts: a.counts });
    }
    return { scores, notes };
}
//# sourceMappingURL=questionScore.js.map